"use client";
import React, { useState, memo, useCallback } from "react";
import dynamic from "next/dynamic";
import Link from "next/link";
import { IoMdMenu } from "react-icons/io";
import { motion } from "framer-motion";
import { MdOutlineKeyboardArrowDown } from "react-icons/md";
import { useRouter } from "next/navigation";
import Image from "next/image";
import { TbArrowBadgeRight } from "react-icons/tb";
import navitems from "@/Data/Navbar";

// Dynamically import UI sheet components with fallback loaders if necessary
const Sheet = dynamic(
  () => import("@/components/ui/sheet").then((mod) => mod.Sheet),
  { ssr: false }
);
const SheetTrigger = dynamic(
  () => import("@/components/ui/sheet").then((mod) => mod.SheetTrigger),
  { ssr: false }
);
const SheetContent = dynamic(
  () => import("@/components/ui/sheet").then((mod) => mod.SheetContent),
  { ssr: false }
);

const Mobilemenu: React.FC = () => {
  const [activeDropdown, setActiveDropdown] = useState<number | null>(null);
  const [innerDropdown, setInnerDropdown] = useState<number | null>(null);
  const router = useRouter();

  const toggleDropdown = useCallback(
    (index: number) => {
      setActiveDropdown((prev) => (prev === index ? null : index));
      // Close any inner dropdown when switching main dropdown
      setInnerDropdown(null);
    },
    []
  );

  const toggleInnerDropdown = useCallback(
    (index: number) => setInnerDropdown((prev) => (prev === index ? null : index)),
    []
  );

  return (
    <div className="text-homeblack py-3 lg:hidden bg-white sticky top-0 z-20">
      <div className="w-full xl:w-[95%] 2xl:w-[80%] mx-auto">
        <div className="text-[26px] lg:hidden cursor-pointer">
          <Sheet>
            <SheetTrigger asChild>
              <IoMdMenu className="text-[28px]" />
            </SheetTrigger>
            <SheetContent side="left" className="max-h-[100vh] py-12 w-[90%] overflow-y-auto">
              {navitems.map((item, index) => (
                <div key={index} className="border-b">
                  {item?.Link ? (
                    <div className="w-full cursor-pointer flex justify-between text-left py-2 px-3 rounded-lg font-semibold">
                      <Link href={item.Link}>{item.title}</Link>
                    </div>
                  ) : (
                    <div
                      onClick={() => toggleDropdown(index)}
                      className="w-full flex cursor-pointer justify-between text-left py-2 px-3 rounded-lg font-semibold"
                    >
                      {item.title}
                      <MdOutlineKeyboardArrowDown
                        className={`${activeDropdown === index ? "rotate-180 duration-200" : "rotate-0"} text-[18px]`}
                      />
                    </div>
                  )}
                  {activeDropdown === index && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="pl-4 overflow-y-auto"
                    >
                      {item?.Element?.map((subItem, subIndex) => (
                        <div key={subIndex}>
                          {subItem?.subtitle ? (
                            <div>
                              <div
                                className="w-full text-left flex justify-between my-1 p-2 rounded-lg font-medium py-2"
                                onClick={() => toggleInnerDropdown(subIndex)}
                              >
                                <div className="flex justify-start gap-3 items-center">
                                  {subItem?.image && (
                                    <div className="bg-lightblue w-[30px] h-[30px] flex items-center justify-center rounded-lg">
                                      <Image
                                        src={subItem?.image}
                                        alt=""
                                        height={20}
                                        width={20}
                                      />
                                    </div>
                                  )}
                                  {subItem?.subtitle}
                                </div>
                                <MdOutlineKeyboardArrowDown
                                  className={`${innerDropdown === subIndex ? "rotate-180 duration-200" : "rotate-0"} text-[18px]`}
                                />
                              </div>
                              {innerDropdown === subIndex && (
                                <motion.div
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: "auto" }}
                                  exit={{ opacity: 0, height: 0 }}
                                  className="pl-4 ml-6 bg-lightblue rounded-lg p-2 overflow-y-auto mb-3"
                                >
                                  {subItem?.links?.map((link, linkIndex) => (
                                    <Link key={linkIndex} href={link.link} className="py-2 flex items-center gap-1 cursor-pointer">
                                      <TbArrowBadgeRight className="text-[20px]" />
                                      {link.title}
                                    </Link>
                                  ))}
                                </motion.div>
                              )}
                            </div>
                          ) : (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              exit={{ opacity: 0, height: 0 }}
                              className="pl-4 bg-lightblue rounded-lg mb-3 p-2 overflow-y-auto"
                            >
                              {subItem?.links?.map((link, linkIndex) => (
                                <Link key={linkIndex} href={link.link} className="py-2 flex items-center gap-1 cursor-pointer">
                                  <TbArrowBadgeRight className="text-[20px]" />
                                  {link.title}
                                </Link>
                              ))}
                            </motion.div>
                          )}
                        </div>
                      ))}
                    </motion.div>
                  )}
                </div>
              ))}
              <div className="my-4 flex flex-wrap gap-2 sm:gap-4">
                <Link
                  target="_blank"
                  href="https://meetings.hubspot.com/sakshi-jass?uuid=ae684c90-b22d-4aed-a972-4de20bef7f87"
                >
                  <button className="max-w-[180px] py-[8px] border-2 px-2 2xl:px-4 border-pink rounded-full meeting-btn">
                    <span className="meeting-text font-semibold text-sm">Book a Meeting</span>
                  </button>
                </Link>
                <div className="w-fit" onClick={() => router.push("/get-a-free-quote")}>
                  <button className="max-w-[180px] py-[8px] border-2 px-2 2xl:px-4 border-pink rounded-full meeting-btn1">
                    <span className="meeting-text font-semibold text-sm">Get a Proposal</span>
                  </button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </div>
  );
};

// Memoize the mobile menu to prevent unnecessary re-renders
export default memo(Mobilemenu);
